import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Search, FileText } from 'lucide-react';
import { db } from '../../../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import ContentViewer from '../../shared/ContentViewer';
import { useBackHandler } from '../../../hooks/NavigationContext';
import { SkeletonList } from '../../shared/Skeleton';
import { isViewableContent } from '../../../utils/educationContent';
import TopicIcon from '../../shared/icons/TopicIcon';
import { labelsFor } from '../../../data/educationTaxonomy';
import { ContentRows } from '../../shared/ContentCard';
import { CollectionRail } from '../../shared/CollectionCard';
import { fetchCollectionsForTopic, collectedItemIds } from '../../../utils/educationCollections';

const EducationTopicScreen = ({ topic, onBack, onCollectionClick }) => {
  // Persist search query for this topic
  const [searchQuery, setSearchQuery] = useLocalStorage(`education-search-${topic.type}`, '');
  const [content, setContent] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContent, setSelectedContent] = useState(null);
  const closeViewer = useCallback(() => setSelectedContent(null), []);
  useBackHandler(Boolean(selectedContent), closeViewer);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (selectedContent && selectedContent.fileType !== 'document') {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      return () => {
        // Restore scroll position
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [selectedContent]);

  // Helper function to force PDF inline viewing instead of download

  useEffect(() => {
    loadContent();
    if (topic.id) fetchCollectionsForTopic(topic.id).then(setCollections).catch(() => setCollections([]));

    // If navigated from search with initial content ID, select it automatically
    if (topic.initialContentId && content.length > 0) {
      const contentToSelect = content.find(item => item.id === topic.initialContentId);
      if (contentToSelect) {
        setSelectedContent(contentToSelect);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic]);

  // Auto-select content when search navigation provides initial content ID
  useEffect(() => {
    if (topic.initialContentId && content.length > 0 && !selectedContent) {
      const contentToSelect = content.find(item => item.id === topic.initialContentId);
      if (contentToSelect) {
        setSelectedContent(contentToSelect);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, topic.initialContentId]);

  const loadContent = async () => {
    try {
      setLoading(true);
      // Items tagged with this topic's id, plus older items that carry only the label
      const col = collection(db, 'education_content');
      const snaps = await Promise.all([
        topic.id ? getDocs(query(col, where('topics', 'array-contains', topic.id))) : null,
        getDocs(query(col, where('category', 'in', topic.id ? labelsFor(topic.id) : [topic.name]))),
      ]);
      const seen = new Set();
      const contentList = [];
      snaps.filter(Boolean).forEach((snap) => snap.forEach((d) => {
        if (seen.has(d.id)) return;
        seen.add(d.id);
        const data = { id: d.id, ...d.data() };
        if (isViewableContent(data)) contentList.push(data);
      }));
      setContent(contentList);
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Items that live in a collection are reached through it, not listed loose
  const inCollection = collectedItemIds(collections);
  const looseContent = content.filter((item) => !inCollection.has(item.id));

  // Group content by subcategory
  const groupedContent = looseContent.reduce((acc, item) => {
    const subcategory = item.subcategory || 'General';
    if (!acc[subcategory]) {
      acc[subcategory] = [];
    }
    acc[subcategory].push(item);
    return acc;
  }, {});

  // Filter content based on search (search looks inside collections too)
  const filteredContent = content.filter(item =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const filteredGroupedContent = filteredContent.reduce((acc, item) => {
    const subcategory = item.subcategory || 'General';
    if (!acc[subcategory]) {
      acc[subcategory] = [];
    }
    acc[subcategory].push(item);
    return acc;
  }, {});




  return (
    <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className={`bg-gradient-to-br ${topic.color} text-white p-6 pb-8`}>
        <button
          onClick={() => {
            onBack();
          }}
          className="flex items-center gap-2 text-white mb-4 hover:opacity-80"
        >
          <ArrowLeft size={20} />
          <span>Back to Education Center</span>
        </button>

        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-white/20 rounded-xl">
            <TopicIcon type={topic.type} className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="font-merriweather text-3xl font-bold">
              {topic.name}
            </h1>
            <p className="text-white/90 text-sm mt-1">
              {topic.description}
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white/20 rounded-xl border-2 border-white/30 focus-within:border-white/60 transition-all">
          <div className="relative">
            <Search className="absolute left-4 top-3.5 text-white/80" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search content..."
              className="w-full pl-12 pr-4 py-3.5 rounded-xl focus:outline-none text-white placeholder-white/70 bg-transparent"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        {loading ? (
          <SkeletonList rows={4} withHeading />
        ) : Object.keys(searchQuery ? filteredGroupedContent : groupedContent).length === 0 && (searchQuery || collections.length === 0) ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <FileText size={32} className="text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="font-bold text-gray-900 dark:text-white mb-2">No Content Yet</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {searchQuery
                ? `No content found matching "${searchQuery}"`
                : "Content for this topic hasn't been uploaded yet. Check back soon!"}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {!searchQuery && collections.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Series & Curricula</h2>
                <CollectionRail collections={collections} onOpen={onCollectionClick} />
              </div>
            )}
            {Object.entries(searchQuery ? filteredGroupedContent : groupedContent).map(([subcategory, items]) => (
              <div key={subcategory}>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">{subcategory}</h2>
                <ContentRows items={items} onOpen={setSelectedContent} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Content Viewer Modal - Only for videos and presentations */}
      {selectedContent && selectedContent.fileType !== 'document' && (
        <ContentViewer
          content={selectedContent}
          accent={topic.color}
          onClose={() => setSelectedContent(null)}
        />
      )}
    </div>
  );
};

export default EducationTopicScreen;
